import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import Calendar from 'react-calendar';
import api from '../../utils/api';
import { getSpecialists } from '../../actions/especialistas';
import { updateCheckout } from '../../actions/checkout';
import { getSpecialistByNombres } from '../../actions/especialistas';
import { SkeletonList } from '../layout/SkeletonCard';
import Toast from '../layout/Toast';
import Modal from '../layout/Modal';
import Spinner from '../layout/Spinner';
import './Calendar.css';

const hasValidName = (value) => typeof value === 'string' && value.trim() !== '';
const toSpecialistSlug = (nombres) =>
  (typeof nombres === 'string' ? nombres : '').trim().replace(/\s+/g, '-');

const AgendaOptions = ({
  auth,
  specialists,
  getSpecialists,
  user,
  updateCheckout,
  getSpecialistByNombres
}) => {
  const [activeTab, setActiveTab] = useState('professional');
  const history = useHistory();

  // States for Por Fecha tab
  const [date, setDate] = useState(new Date(Date.now() + 86400000));
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [toast, setToast] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    getSpecialists();
  }, [getSpecialists]);

  useEffect(() => {
    if (activeTab === 'date') {
      getScheduleForDate(date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, activeTab]);

  const getScheduleForDate = async (selectedDate) => {
    setLoading(true);
    setError('');
    setSchedule([]);
    setSelectedSlot(null);
    try {
      const dateString = selectedDate.toISOString().split('T')[0];
      const res = await api.get(`/available?date=${dateString}`);
      setSchedule(res.data);
      if (res.data.length > 0 && !availableDates.includes(dateString)) {
        setAvailableDates([...availableDates, dateString]);
      }
    } catch (err) {
      console.error(err);
      setError('No se pudo cargar la disponibilidad. Por favor, intente de nuevo.');
    }
    setLoading(false);
  };

  const tileClassName = ({ date, view }) => {
    if (view === 'month') {
      const dateString = date.toISOString().split('T')[0];
      return availableDates.includes(dateString) ? 'has-availability' : null;
    }
  };

  const onDateChange = (newDate) => {
    setDate(newDate);
  };

  const formatTime = (isoString) => {
    return new Date(isoString).toLocaleTimeString('es-PE', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleSelectSlot = (time, specialistId) => {
    setSelectedSlot({ time, specialistId });
  };

  const handleReservarClick = () => {
    if (!auth.isAuthenticated) {
      history.push('/login');
      return;
    }
    if (!selectedSlot) {
      setToast({ message: 'Por favor selecciona un terapeuta y horario', type: 'warning' });
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmReservation = async () => {
    setShowConfirmModal(false);
    try {
      let selectedSpecialist = null;
      for (const slot of schedule) {
        if (slot.time === selectedSlot.time) {
          selectedSpecialist = slot.specialists.find(s => s._id === selectedSlot.specialistId);
          break;
        }
      }
      if (!selectedSpecialist) {
        setToast({ message: 'No se encontró el especialista seleccionado', type: 'error' });
        return;
      }
      if (!hasValidName(selectedSpecialist.nombres)) {
        setToast({ message: 'El terapeuta seleccionado no tiene nombre válido', type: 'error' });
        return;
      }
      await getSpecialistByNombres(selectedSpecialist.nombres);
      updateCheckout({
        cliente: user._id,
        newSession: true,
        especialista: selectedSpecialist._id,
        tarifa: selectedSpecialist.tarifas,
        horaPactada: new Date(selectedSlot.time),
        reagendo: false,
      });
      history.push('/casiacabamos');
    } catch (err) {
      console.error('Error al reservar:', err);
      setToast({ message: 'Hubo un error al procesar tu reserva. Por favor, intenta de nuevo.', type: 'error' });
    }
  };

  const getSelectedSpecialist = () => {
    if (!selectedSlot) return null;
    for (const slot of schedule) {
      if (slot.time === selectedSlot.time) {
        return slot.specialists.find(s => s._id === selectedSlot.specialistId);
      }
    }
    return null;
  };

  return (
    <section className="flex-grow flex flex-col mx-auto font-sans mb-6 w-full">
      <div className="w-full relative">
        <div className="mx-auto text-center text-white bg-higia-azul">
          <div className="container mx-auto px-8">
            <h2 className="pt-4 md:pt-12 text-3xl font-extrabold mx-3">
              ¡Ellos están aquí para ti!
            </h2>
            <p className="text-xl font-bold mx-3 pb-6">
              Disponte a escucharte y ayudarte.
            </p>
          </div>
        </div>
        <div
          className="text-higia-azul overflow-hidden relative"
          style={{ top: "-2px" }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1440 320"
            className="fill-current shadow-lg w-full wavesShadow"
            preserveAspectRatio="none"
          >
            <path d="M0,128L80,138.7C160,149,320,171,480,154.7C640,139,800,85,960,85.3C1120,85,1280,139,1360,165.3L1440,192L1440,0L1360,0C1280,0,1120,0,960,0C800,0,640,0,480,0C320,0,160,0,80,0L0,0Z"></path>
          </svg>
        </div>
      </div>

      {/* Tabs */}
      <div className="container mx-auto px-8">
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setActiveTab('professional')}
            className={`px-8 py-3 rounded-t-lg font-semibold text-lg transition-all ${
              activeTab === 'professional'
                ? 'bg-higia-cyan text-white shadow-md'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Por profesional
          </button>
          <button
            onClick={() => setActiveTab('date')}
            className={`px-8 py-3 rounded-t-lg font-semibold text-lg transition-all ${
              activeTab === 'date'
                ? 'bg-higia-cyan text-white shadow-md'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Por fecha
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'professional' ? (
          <div className="bg-white rounded-lg shadow-lg p-6">
            {specialists.loading || !specialists.specialists ? (
              <Spinner />
            ) : (
              <div className="space-y-4">
                {specialists.specialists
                  .filter(specialist => !specialist.isInactive && hasValidName(specialist.nombres))
                  .map((specialist, idx) => (
                    <ProfessionalCard key={idx} info={specialist} />
                  ))}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex flex-col md:flex-row md:space-x-8">
              <div className="md:w-1/3 flex justify-center mb-6 md:mb-0">
                <Calendar
                  onChange={onDateChange}
                  value={date}
                  minDate={new Date()}
                  locale="es-ES"
                  tileClassName={tileClassName}
                />
              </div>

              <div className="md:w-2/3">
                <h2 className="text-2xl font-bold mb-6 text-gray-900">
                  {date.toLocaleDateString('es-ES', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </h2>

                {loading ? (
                  <SkeletonList count={4} />
                ) : error ? (
                  <div className="text-red-500 bg-red-100 p-4 rounded">{error}</div>
                ) : schedule.length > 0 ? (
                  <>
                    <div className="space-y-6 mb-8">
                      {schedule.map(({ time, specialists: timeSpecialists }) => (
                        <div key={time} className="mb-6">
                          <h3 className="text-lg font-bold text-gray-800 bg-gray-100 p-3 rounded-lg mb-4">
                            {formatTime(time)}
                          </h3>
                          <div className="space-y-3">
                            {timeSpecialists.map(specialist => {
                              const isSelected = selectedSlot?.time === time && selectedSlot?.specialistId === specialist._id;
                              const specialistUrl = `/terapeutas/${toSpecialistSlug(specialist.nombres)}`;
                              return (
                                <div
                                  key={specialist._id}
                                  className={`bg-white p-5 rounded-xl shadow-md transition-all ${
                                    isSelected ? 'ring-2 ring-gray-800 border-2 border-gray-800 shadow-lg' : 'hover:shadow-lg border border-gray-200'
                                  }`}
                                >
                                  <div className="flex items-start">
                                    <div
                                      className="mr-4 mt-1 cursor-pointer"
                                      onClick={() => handleSelectSlot(time, specialist._id)}
                                    >
                                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                        isSelected ? 'border-gray-800 bg-gray-50' : 'border-gray-400'
                                      }`}>
                                        {isSelected && (
                                          <div className="w-3.5 h-3.5 rounded-full bg-gray-800"></div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-start flex-1">
                                      <Link to={specialistUrl}>
                                        <img
                                          className="h-20 w-20 object-cover rounded-full cursor-pointer hover:opacity-90 transition border-2 border-gray-200"
                                          src={specialist.avatar || '/default-avatar.png'}
                                          alt="Foto de Perfil"
                                          onError={(e) => {
                                            e.target.src = '/default-avatar.png';
                                          }}
                                        />
                                      </Link>
                                      <div className="ml-4 flex-1">
                                        <div className="flex items-start justify-between">
                                          <div>
                                            <h4 className="text-lg font-bold text-gray-900">{specialist.nombres}</h4>
                                            {specialist.especialidad && (
                                              <p className="text-xs text-gray-500 mt-0.5">{specialist.especialidad}</p>
                                            )}
                                          </div>
                                          {specialist.numSes && (
                                            <div className="flex flex-col items-center text-higia-cyan ml-3">
                                              <svg
                                                className="h-4 w-4 fill-current"
                                                viewBox="0 0 32 29.6"
                                              >
                                                <path d="M23.6,0c-3.4,0-6.3,2.7-7.6,5.6C14.7,2.7,11.8,0,8.4,0C3.8,0,0,3.8,0,8.4c0,9.4,9.5,11.9,16,21.2c6.1-9.3,16-12.1,16-21.2C32,3.8,28.2,0,23.6,0z" />
                                              </svg>
                                              <div className="text-gray-800 text-xs font-semibold">{specialist.numSes}</div>
                                            </div>
                                          )}
                                        </div>
                                        {specialist.longDesc && (
                                          <p className="text-sm text-gray-600 mt-2 line-clamp-2 leading-relaxed">{specialist.longDesc}</p>
                                        )}
                                        {specialist.tarifas && Object.keys(specialist.tarifas).length > 0 && (
                                          <div className="flex items-center justify-between mt-3">
                                            <div className="text-sm">
                                              {Object.keys(specialist.tarifas).map((serv, idx) => (
                                                <span key={serv} className="text-gray-900 font-bold">
                                                  {idx > 0 && ' • '}
                                                  S/{specialist.tarifas[serv]}
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="sticky bottom-0 bg-white p-4 border-t border-gray-200 shadow-xl">
                      <button
                        onClick={handleReservarClick}
                        disabled={!selectedSlot}
                        className={`w-full py-4 px-6 rounded-xl font-bold text-lg transition-all transform ${
                          selectedSlot
                            ? 'bg-gray-900 text-white hover:bg-gray-800 shadow-lg hover:shadow-xl hover:-translate-y-0.5'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {selectedSlot ? 'Confirmar y Reservar Cita' : 'Selecciona un terapeuta y horario'}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="bg-gray-50 p-6 rounded-lg text-center">
                    <p className="text-gray-700">No hay horarios disponibles para este día.</p>
                    <p className="text-gray-500 text-sm">Por favor, selecciona otra fecha.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmReservation}
        title="Confirmar Reserva"
        confirmText="Confirmar Reserva"
        cancelText="Cancelar"
      >
        {selectedSlot && getSelectedSpecialist() && (
          <div className="space-y-3">
            <div className="flex items-center pb-3 border-b">
              <img
                className="h-16 w-16 object-cover rounded-full mr-4"
                src={getSelectedSpecialist().avatar || '/default-avatar.png'}
                alt="Terapeuta"
              />
              <div>
                <p className="font-bold text-lg">{getSelectedSpecialist().nombres}</p>
                {getSelectedSpecialist().especialidad && (
                  <p className="text-sm text-gray-600">{getSelectedSpecialist().especialidad}</p>
                )}
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-600">Fecha y hora</p>
              <p className="font-semibold">{new Date(selectedSlot.time).toLocaleString('es-PE', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
              })}</p>
            </div>
            {getSelectedSpecialist().tarifas && (
              <div>
                <p className="text-sm text-gray-600">Tarifa</p>
                <p className="font-semibold text-higia-bermuda">
                  {Object.keys(getSelectedSpecialist().tarifas).map((serv, idx) => (
                    <span key={serv}>
                      {idx > 0 && ' • '}
                      S/{getSelectedSpecialist().tarifas[serv]}
                    </span>
                  ))}
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </section>
  );
};

const ProfessionalCard = ({ info }) => {
  const specialistName = hasValidName(info.nombres) ? info.nombres : 'Especialista';
  const specialistSlug = toSpecialistSlug(info.nombres);

  return (
    <div className="bg-white p-4 rounded-xl border-2 border-higia-bermuda shadow-md hover:shadow-lg transition-all">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 flex-1">
          <Link to={`/terapeutas/${specialistSlug}`}>
            <img
              className="h-16 w-16 object-cover rounded-full"
              src={info.avatar}
              alt={`Foto de ${specialistName}`}
            />
          </Link>
          <div className="flex-1">
            <Link to={`/terapeutas/${specialistSlug}`}>
              <h4 className="text-lg font-bold text-gray-900 hover:text-higia-bermuda transition-colors">
                {specialistName}
              </h4>
            </Link>
            {info.especialidad && (
              <p className="text-sm text-gray-600">
                {info.especialidad}
              </p>
            )}
            {info.desc && (
              <p className="text-xs text-gray-500 mt-1">
                {info.desc}
              </p>
            )}
            {info.tarifas && Object.keys(info.tarifas).length > 0 && (
              <div className="mt-2">
                <p className="text-sm font-semibold text-gray-900">
                  {Object.keys(info.tarifas).map((serv, idx) => (
                    <span key={serv}>
                      {idx > 0 && ' • '}
                      S/{info.tarifas[serv]}
                    </span>
                  ))}
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4">
          {info.numSes && (
            <div className="flex flex-col items-center text-higia-cyan">
              <svg
                className="h-6 w-6 fill-current"
                viewBox="0 0 32 29.6"
              >
                <path d="M23.6,0c-3.4,0-6.3,2.7-7.6,5.6C14.7,2.7,11.8,0,8.4,0C3.8,0,0,3.8,0,8.4c0,9.4,9.5,11.9,16,21.2c6.1-9.3,16-12.1,16-21.2C32,3.8,28.2,0,23.6,0z" />
              </svg>
              <div className="text-gray-900 text-sm font-bold mt-1">{info.numSes}</div>
            </div>
          )}
          <Link to={`/terapeutas/${specialistSlug}/horarios`}>
            <button className="bg-higia-bermuda text-white px-6 py-2 rounded-lg font-semibold hover:bg-opacity-90 transition-all">
              Agenda aquí
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  specialists: state.specialists,
  auth: state.auth,
  user: state.auth.user,
});

export default connect(mapStateToProps, {
  getSpecialists,
  updateCheckout,
  getSpecialistByNombres
})(AgendaOptions);
